/**
 * Menu Builder Page
 * 
 * Visual drag-and-drop menu builder for creating custom menu layouts.
 * Allows restaurants to design their menu experience while maintaining
 * operational data integrity (prices, availability always from database).
 */

import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useBranch } from '@/contexts/BranchContext';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { useMenuBuilderStore } from '@/stores/menuBuilderStore';
import { useMenuBuilderLayout } from '@/hooks/useMenuBuilderLayout';
import { createEmptyLayout } from '@/types/menuBuilder';

// Import builder components
import { BuilderToolbar } from '@/components/menu-builder/layout/BuilderToolbar';
import { BuilderToolbox } from '@/components/menu-builder/layout/BuilderToolbox';
import { BuilderCanvas } from '@/components/menu-builder/layout/BuilderCanvas';
import { PropertiesPanel } from '@/components/menu-builder/properties/PropertiesPanel';

// Import preview dialog
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BlockRenderer } from '@/components/menu-builder/blocks/BlockRenderer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useGlobalThemeStyles } from '@/hooks/useGlobalThemeStyles';

const MenuBuilder = () => {
  const { t } = useTranslation();
  const { selectedBranchId, setSelectedBranchId } = useBranch();
  const { branches, loading: branchesLoading } = useFilteredBranches();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Get current branch from branches list
  const currentBranch = useMemo(() => {
    return branches.find(b => b.id === selectedBranchId) || null;
  }, [branches, selectedBranchId]);

  // Get store actions and state
  const loadLayout = useMenuBuilderStore((s) => s.loadLayout);
  const getLayout = useMenuBuilderStore((s) => s.getLayout);
  const markClean = useMenuBuilderStore((s) => s.markClean);
  const blocks = useMenuBuilderStore((s) => s.blocks);
  const globalTheme = useMenuBuilderStore((s) => s.globalThemeOverrides);
  const isDirty = useMenuBuilderStore((s) => s.isDirty);
  const backgroundStyle = useGlobalThemeStyles(globalTheme);

  // Track if we've loaded for this branch to prevent infinite loops
  const loadedBranchRef = useRef<string | null>(null);

  // TanStack Query hooks for data persistence
  const {
    layout: savedLayout,
    layoutRecord,
    isLoading,
    isSaving,
    isPublishing,
    isPublished,
    saveLayout,
    publishLayout,
  } = useMenuBuilderLayout(selectedBranchId || '');

  // Load saved layout on mount or when branch changes
  useEffect(() => {
    // Only load if we haven't loaded for this branch yet
    if (isLoading) return;
    if (loadedBranchRef.current === selectedBranchId) return;
    
    if (savedLayout) {
      loadLayout(savedLayout);
      loadedBranchRef.current = selectedBranchId;
    } else if (selectedBranchId) {
      // No saved layout, start with empty
      loadLayout(createEmptyLayout());
      loadedBranchRef.current = selectedBranchId;
    }
  }, [savedLayout, isLoading, selectedBranchId, loadLayout]);

  // Handle save
  const handleSave = useCallback((options?: { suppressToast?: boolean }) => {
    if (!selectedBranchId) return;

    const currentLayout = getLayout();
    saveLayout(
      { layout: currentLayout, suppressToast: options?.suppressToast },
      {
        onSuccess: () => {
          markClean();
        },
      }
    );
  }, [selectedBranchId, getLayout, saveLayout, markClean]);

  // Auto-save effect
  useEffect(() => {
    if (!isDirty || !selectedBranchId || isSaving) return;

    const timer = setTimeout(() => {
      handleSave({ suppressToast: true });
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => clearTimeout(timer);
  }, [isDirty, selectedBranchId, isSaving, handleSave]);

  // Warn on unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Handle publish
  const handlePublish = useCallback(() => {
    if (!selectedBranchId) return;

    // First save, then publish
    const currentLayout = getLayout();
    saveLayout(
      { layout: currentLayout },
      {
        onSuccess: () => {
          markClean();
          publishLayout();
        },
      }
    );
  }, [selectedBranchId, getLayout, saveLayout, publishLayout, markClean]);

  // Handle preview
  const handlePreview = useCallback(() => {
    setIsPreviewOpen(true);
  }, []);

  if (branchesLoading) {
    return (
      <DashboardLayout>
        <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
          <Skeleton className="h-12 w-48" />
        </div>
      </DashboardLayout>
    );
  }

  if (!currentBranch) {
    return (
      <DashboardLayout>
        <div className="h-[calc(100vh-4rem)] flex flex-col">
          <BuilderToolbar
            onSave={() => {}}
            onPublish={() => {}}
            onPreview={() => {}}
            branches={branches}
            selectedBranchId={selectedBranchId}
            onBranchChange={setSelectedBranchId}
          />
          <div className="flex-1 flex items-center justify-center">
            <p className="text-muted-foreground">
              {t('menuBuilder.selectBranch', 'Please select a branch to use the Menu Builder')}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <TooltipProvider>
      <DashboardLayout>
        <div className="h-[calc(100vh-4rem)] flex flex-col">
          {/* Toolbar */}
          <BuilderToolbar
            onSave={() => handleSave()}
            onPublish={handlePublish}
            onPreview={handlePreview}
            isSaving={isSaving}
            isPublishing={isPublishing}
            isPublished={isPublished}
            branches={branches}
            selectedBranchId={selectedBranchId}
            onBranchChange={setSelectedBranchId}
          />

          {/* Main Workspace */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Sidebar - Block Toolbox */}
            <BuilderToolbox className="w-64" />

            {/* Center - Canvas */}
            <BuilderCanvas className="flex-1" />

            {/* Right Sidebar - Properties Panel */}
            <PropertiesPanel className="w-80" />
          </div>
        </div>

        {/* Preview Dialog */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-[400px] h-[700px] p-0 overflow-hidden">
            <DialogHeader className="p-4 border-b">
              <DialogTitle>{t('menuBuilder.preview', 'Preview')}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="flex-1 h-[calc(100%-60px)]">
              <div 
                className="p-4 space-y-2 min-h-full"
                style={backgroundStyle}
              >
                {blocks.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {t('menuBuilder.noBlocks', 'No blocks to preview')}
                  </p>
                ) : (
                  blocks.map((block) => (
                    <BlockRenderer
                      key={block.id}
                      block={block}
                      isPreview={true}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    </TooltipProvider>
  );
};

export default MenuBuilder;
